import React from 'react';
import moment from 'moment';
import { Box, Tooltip } from '@mui/material';
import {
  TooltipLessonHeader,
  TooltipLessonTitle,
  TooltipStatusBadge,
  TooltipBody,
  TooltipInfoRow,
  TooltipInfoLabel,
  TooltipInfoValue,
} from '../groupLesson/GroupLessonStyled';
import {
  ATTEND_STATUS_META,
  GROUP_CARD_META,
  PrivateLessonCard,
  GroupLessonCard,
  ConsultationCard,
  DuetLessonCard,
  EventCardText,
  DayCardStatusBadge,
  EVENT_TYPE_META,
  OT_CARD_META,
  CONSULT_CARD_META,
  CONSULT_RESULT_META,
  CONSULT_TARGET_META,
  ConsultTargetChip,
  ETC_CARD_META,
  MIN_CARD_EXPOSED_HEIGHT,
} from './PrivateLessonStyled';
import LessonHoverTooltip from './LessonHoverTooltip';
import { getLessonAttendStatus, getFilteredStatusCardMeta, isOtGroupEvent, getEventDisplayName } from './lessonStatus';
import { layoutDayEvents, layoutStackedOffsets } from './eventOverlapLayout';
import { timeToIndex, clamp, calculateDuration, getEventCardKey } from './hooks/usePrivateLessonData';

/**
 * 이벤트 위치 계산 — hourIndex 는 칸 시작 시각(시, 30분 칸이면 9.5 처럼 소수), slotHours 는 칸 길이(시).
 * 퍼센트는 칸(EventCell) 높이 기준이다. minHeightPx 가 있으면 카드 최소 높이로 함께 돌려준다.
 */
export const calculateEventPosition = (event, hourIndex, periodTimes, topOffsetPx = 0, slotHours = 1, minHeightPx) => {
  const eventStartHour = timeToIndex(event.start);
  const eventEndHour = timeToIndex(event.end);

  const endTimeLimit = periodTimes?.endTime ? Number(periodTimes.endTime) : 24;
  const startTimeLimit = periodTimes?.startTime ? Number(periodTimes.startTime) : 0;

  // 시작 시각이 마감 이후일 때만 제외 (끝나는 시각은 clamp로 처리, 그룹 캘린더와 동일)
  if (Math.floor(eventStartHour) > endTimeLimit) {
    return null;
  }

  const clampedStart = clamp(eventStartHour, 0, 24);
  const clampedEnd = clamp(eventEndHour, 0, 24);

  // hourIndex가 설정된 시간 범위 내에 있고, 이벤트가 해당 시간대와 겹치는 경우만 렌더링
  // (마지막 행은 종료 시 행이라 칸 시작이 종료 시 + 1 보다 앞이면 범위 안이다)
  if (
    !(
      hourIndex >= startTimeLimit &&
      hourIndex < endTimeLimit + 1 &&
      clampedStart <= hourIndex + slotHours &&
      clampedEnd > hourIndex
    )
  ) {
    return null;
  }

  const topPercent = ((clampedStart - hourIndex) / slotHours) * 100;

  return {
    height: `${((clampedEnd - clampedStart) / slotHours) * 100}%`,
    minHeight: minHeightPx != null ? `${minHeightPx}px` : undefined,
    // topOffsetPx: 앞 카드의 시간 줄을 가리지 않도록 아래로 밀어낸 값 (겹치지 않으면 0)
    top: topOffsetPx ? `calc(${topPercent}% + ${topOffsetPx}px)` : `${topPercent}%`,
  };
};

// 일간 카드 공통 프레임 스타일 (진한 상태색 배경 + 흰 글씨, 2줄: 뱃지·시간 / 회원명·부가정보)
// 회원명은 대체 수단이 없으므로 절대 숨기지 않는다. 카드가 낮아지면 글자를 줄여서 2줄을 지키고,
// 두 줄이 물리적으로 불가능한 높이에서만 시간을 접고 [뱃지][회원명] 한 줄로 합친다.
// 글자 크기 = max(가독 하한 px, min(가로 축소용 em, 세로 축소용 cqh)).
//  - em  : 분할 카드(compactCardStyle)의 폭 기반 축소를 그대로 따라간다.
//  - cqh : 카드 높이에 비례해 연속적으로 줄어든다(줌 배율·모니터 크기 변화에 계단 없이 대응).
// containerType:'size'의 크기 질의는 content-box 기준이라 아래 임계값에 padding(2px×2)·border(1px×2)는 포함되지 않는다.
// (카드 raw 높이 ≈ 질의값 + 6px)
const dayCardFrameStyle = (meta, extra = {}) => ({
  flexDirection: 'column',
  // 위쪽 정렬 — 뒤 일정이 카드를 덮어도 첫 줄(시간)은 남아서 무슨 일정인지 읽힌다
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  gap: '2px',
  padding: '2px 7px',
  borderRadius: '6px',
  border: `1px solid ${meta.dayBorder}`,
  containerType: 'size',
  '.dc-top': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 700,
    fontSize: 'max(9px, min(1.05em, 40cqh))',
    lineHeight: 1.18,
  },
  // 시간이 카드의 주역 — 카드가 겹쳐 아래가 가려져도 첫 줄만 보이면 어떤 일정인지 식별된다
  '.dc-time': { fontWeight: 800, fontSize: 'max(10.5px, min(1.2em, 46cqh))', letterSpacing: '-0.1px' },
  '.dc-sub': { fontSize: 'max(8.5px, min(0.9em, 34cqh))', lineHeight: 1.2 },
  // 잔여 세션은 시간행 오른쪽 끝에 붙인다 (크기는 시간행 기본값을 상속 — 시간만 한 단계 크다)
  '.dc-session': { marginLeft: 'auto', paddingLeft: '6px', whiteSpace: 'nowrap' },
  // 뱃지는 시간에 주역을 내주고 한 단계 물러난다. 행 높이를 넘지 않도록 cqh 상한은 계속 공유한다.
  '.dc-badge': {
    fontSize: 'max(8.5px, min(0.85em, 32cqh))',
    lineHeight: 1.3,
    padding: '1px 6px',
    borderRadius: '6px',
    fontWeight: 700,
  },
  '@container (max-width: 190px)': {
    '.dc-room': { display: 'none' },
  },
  '@container (max-width: 120px)': {
    '.dc-session': { display: 'none' },
  },
  // 2줄 최소 높이(뱃지행 약 10.8px + gap 2px + 회원명행 약 10.2px ≈ 23px) 미만 →
  // 시간은 카드의 세로 위치로 유추 가능하므로 시간만 접고 회원명은 뱃지 옆에 한 줄로 남긴다.
  '@container (max-height: 22px)': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '4px',
    '.dc-time': { display: 'none' },
    '.dc-top': { width: 'auto', flex: '0 0 auto' },
    '.dc-sub': { width: 'auto', flex: '1 1 auto', minWidth: 0 },
  },
  ...extra,
});

// 카드 1행 (출석상태 뱃지 + 시간 + 우측 끝 잔여 세션) — 수강권 임박 여부는 카드가 아닌 툴팁에서만 표시한다
const DayCardTopRow = ({ meta, timeText, statusLabel, rightBadge, sessionCounts }) => (
  <EventCardText className="dc-top" fontColor={meta.dayFont}>
    {statusLabel && (
      <DayCardStatusBadge className="dc-badge" badgeBg={meta.badgeBg} badgeFont={meta.badgeFont}>
        {statusLabel}
      </DayCardStatusBadge>
    )}
    <span className="dc-time" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {timeText}
    </span>
    {sessionCounts && <span className="dc-session">세션 {sessionCounts}</span>}
    {rightBadge}
  </EventCardText>
);

// 카드 2행 — 회원명 / 부가정보 (좁아지면 부가정보부터 접힘)
const DayCardSubRow = ({ meta, name, roomName }) => (
  <EventCardText className="dc-sub" fontColor={meta.dayFont}>
    <b>{name}</b>
    {roomName && <span className="dc-room"> / {roomName}</span>}
  </EventCardText>
);

/**
 * 개인 레슨 카드 — 강사명 없이 시간/회원명/수강권·세션, 출석 상태별 배경색 (시안 확정)
 */
const PrivateLessonCardComponent = ({
  event,
  position,
  onClick,
  onContextMenu,
  onDragStart,
  isDragging,
  splitInfo,
  ticketAlertCount,
  ticketAlertCountUnit,
}) => {
  const status = getLessonAttendStatus(event);
  const meta = ATTEND_STATUS_META[status];
  const memberName = event.memberName || '';

  // 개인레슨 수강권 잔여/총 세션 (백엔드가 PRIVATE_LESSON일 때만 내려줌)
  const hasSessionInfo = typeof event.totalSessionCount === 'number' && typeof event.remainingSessionCount === 'number';
  const sessionCounts = hasSessionInfo ? `${event.remainingSessionCount}/${event.totalSessionCount}` : '';

  const total = splitInfo?.total || 1;
  const splitIndex = splitInfo?.index || 0;
  const cardWidth = total > 1 ? `${100 / total}%` : '100%';
  const cardLeft = total > 1 ? `${(splitIndex * 100) / total}%` : 0;
  const showBadge = total < 4; // 4분할 이상은 폭이 좁아 뱃지 생략

  const compactCardStyle = {
    ...(isDragging ? { opacity: 0.4, cursor: 'grabbing' } : { cursor: 'grab' }),
    ...(total === 2 && { fontSize: '0.65rem' }),
    ...(total === 3 && { padding: '2px 4px', fontSize: '0.6rem' }),
    ...(total >= 4 && { padding: '2px 3px', fontSize: '0.55rem' }),
  };

  return (
    <LessonHoverTooltip event={event} ticketAlertCount={ticketAlertCount} ticketAlertCountUnit={ticketAlertCountUnit}>
      <PrivateLessonCard
        className="event-card"
        data-class-time-id={event.courseClassTimeId}
        data-course-class-id={event.courseClassId}
        bgColor={meta.dayBg}
        cardHeight={position.height}
        cardTop={position.top}
        cardMinHeight={position.minHeight}
        cardWidth={cardWidth}
        cardLeft={cardLeft}
        cardZIndex={2 + splitIndex}
        sx={dayCardFrameStyle(meta, compactCardStyle)}
        onMouseDown={e => {
          e.stopPropagation();
          onDragStart?.(e, event);
        }}
        onClick={e => onClick(e)}
        onContextMenu={e => onContextMenu?.(e, event)}>
        <DayCardTopRow
          meta={meta}
          timeText={total >= 4 ? event.startTime : `${event.startTime} - ${event.endTime}`}
          statusLabel={showBadge ? meta.label : null}
          sessionCounts={sessionCounts}
        />
        <DayCardSubRow meta={meta} name={memberName} roomName={event.branchExerciseRoomName} />
      </PrivateLessonCard>
    </LessonHoverTooltip>
  );
};

/**
 * 듀엣 레슨 카드 — 회원별 분할 유지, 출석 상태색·툴팁은 개인레슨과 동일 취급
 */
const DuetLessonCardComponent = ({ event, position, onClick, onContextMenu, onDragStart, isDragging, ticketAlertCount, ticketAlertCountUnit, splitInfo, typeBadge, cardMeta }) => {
  const members = event.members || [];
  const status = getLessonAttendStatus(event);
  // cardMeta: 유형별 배경 덮어쓰기(OT). 상태 라벨·배지는 출석 상태 메타 그대로.
  const meta = { ...ATTEND_STATUS_META[status], ...cardMeta };
  const hasSessionInfo = typeof event.totalSessionCount === 'number' && typeof event.remainingSessionCount === 'number';
  const sessionCounts = hasSessionInfo ? `${event.remainingSessionCount}/${event.totalSessionCount}` : '';

  if (members.length === 0) {
    // members가 없으면 개인 레슨처럼 렌더링
    return (
      <PrivateLessonCardComponent
        event={event}
        position={position}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onDragStart={onDragStart}
        isDragging={isDragging}
        ticketAlertCount={ticketAlertCount}
        ticketAlertCountUnit={ticketAlertCountUnit}
        splitInfo={splitInfo}
      />
    );
  }

  // 겹침 분할 슬롯 — 회원별 분할(cardWidth/cardLeft)을 이 슬롯 폭 안으로 축소한다
  const splitTotal = splitInfo?.total || 1;
  const splitIndex = splitInfo?.index || 0;
  const slotLeft = (splitIndex * 100) / splitTotal;

  return members.map((member, memberIdx) => {
    const totalMembers = members.length;
    const memberWidth = totalMembers > 1 ? 100 - (memberIdx * 100) / totalMembers : 100;
    const memberLeft = totalMembers > 1 ? (memberIdx * 100) / totalMembers : 0;
    const cardWidth = memberWidth / splitTotal;
    const cardLeft = slotLeft + memberLeft / splitTotal;

    return (
      <LessonHoverTooltip key={`duet-${memberIdx}`} event={event} ticketAlertCount={ticketAlertCount} ticketAlertCountUnit={ticketAlertCountUnit}>
        <DuetLessonCard
          className="event-card"
          data-class-time-id={memberIdx === 0 ? event.courseClassTimeId : undefined}
          data-course-class-id={memberIdx === 0 ? event.courseClassId : undefined}
          bgColor={meta.dayBg}
          cardHeight={position.height}
          cardTop={position.top}
          cardMinHeight={position.minHeight}
          cardWidth={`${cardWidth}%`}
          cardLeft={`${cardLeft}%`}
          cardZIndex={2 + splitIndex + memberIdx}
          isOverlay={memberIdx > 0}
          sx={dayCardFrameStyle(meta, isDragging ? { opacity: 0.4, cursor: 'grabbing' } : { cursor: 'grab' })}
          onMouseDown={e => {
            e.stopPropagation();
            onDragStart?.(e, event);
          }}
          onClick={e => onClick(e, member)}
          onContextMenu={e => onContextMenu?.(e, event)}>
          <DayCardTopRow
            meta={meta}
            timeText={`${event.startTime} - ${event.endTime}`}
            statusLabel={meta.label}
            sessionCounts={sessionCounts}
            rightBadge={typeBadge}
          />
          <DayCardSubRow meta={meta} name={member.memberName} roomName={event.branchExerciseRoomName} />
        </DuetLessonCard>
      </LessonHoverTooltip>
    );
  });
};

/**
 * 그룹 레슨 카드 (개인 스케줄 내) — 시안: 시간+인원 뱃지 / 그룹레슨 (강사명) / 수업명
 */
const GroupLessonCardComponent = ({ event, position, onContextMenu, splitInfo, typeFilter }) => {
  // 그룹만 보는 중이면 예약 상태색(카운트 칩과 동일), 그 외에는 중립 회색
  const meta = getFilteredStatusCardMeta(event, typeFilter) || GROUP_CARD_META;
  const totalMemberCount =
    (event.reservedMemberCount || 0) + (event.attendedMemberCount || 0) + (event.absentedMemberCount || 0);

  // 같은 시간대 겹침 시 좌우 분할
  const total = splitInfo?.total || 1;
  const splitIndex = splitInfo?.index || 0;

  return (
    <LessonHoverTooltip event={event}>
      <GroupLessonCard
        className="event-card"
        data-class-time-id={event.courseClassTimeId}
        data-course-class-id={event.courseClassId}
        bgColor={meta.dayBg}
        cardHeight={position.height}
        cardTop={position.top}
        cardMinHeight={position.minHeight}
        cardWidth={total > 1 ? `${100 / total}%` : '100%'}
        cardLeft={total > 1 ? `${(splitIndex * 100) / total}%` : 0}
        cardZIndex={2 + splitIndex}
        sx={dayCardFrameStyle(meta)}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        onContextMenu={e => onContextMenu?.(e, event)}>
        <DayCardTopRow
          meta={meta}
          timeText={`${event.startTime} - ${event.endTime}`}
          rightBadge={
            <DayCardStatusBadge className="dc-badge dc-session" badgeBg={meta.badgeBg} badgeFont={meta.badgeFont || meta.dayFont}>
              {totalMemberCount}/{event.membersMaxCount}명
            </DayCardStatusBadge>
          }
        />
        {/* 일간 보기는 강사 컬럼 구조라 카드 내 강사명은 생략 */}
        <DayCardSubRow meta={meta} name="그룹레슨" roomName={event.className} />
      </GroupLessonCard>
    </LessonHoverTooltip>
  );
};

// 여러 명 OT 호버 툴팁 — 그룹레슨 캘린더 툴팁과 같은 모양·상태 규칙(종료/예약마감/예약없음/예약미달), 대기열 없음
const OT_GROUP_STATUS = {
  past: { badge: '#BFBFBF', font: '#404040', label: '종료' },
  full: { badge: '#A7E3BE', font: '#247E46', label: '예약마감' },
  none: { badge: '#F6B6A9', font: '#9E230B', label: '예약없음' },
  under: { badge: '#FFD9A9', font: '#AA6000', label: '예약미달' },
};

const getOtGroupStatus = event => {
  const reserved = event.reservedMemberCount || 0;
  const attended = event.attendedMemberCount || 0;
  const absented = event.absentedMemberCount || 0;
  if (moment().isAfter(moment(`${event.registrationStartDate} ${event.endTime}`, 'YYYY-MM-DD HH:mm'))) return 'past';
  if (reserved + attended >= (event.membersMaxCount || 0)) return 'full';
  if (reserved === 0 && attended === 0 && absented === 0) return 'none';
  return 'under';
};

const OT_GROUP_TOOLTIP_PROPS = {
  arrow: true,
  placement: 'right',
  disableInteractive: true,
  componentsProps: {
    popper: { sx: { pointerEvents: 'none' } },
    tooltip: {
      sx: {
        bgcolor: '#fff',
        color: '#333',
        borderRadius: '10px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        minWidth: '210px',
        p: '10px',
        border: '1px solid #E0E3E6',
        pointerEvents: 'none',
      },
    },
    arrow: { sx: { color: '#fff', '&::before': { border: '1px solid #E0E3E6' } } },
  },
};

const OtGroupTooltipContent = ({ event }) => {
  const status = OT_GROUP_STATUS[getOtGroupStatus(event)];
  const currentCount = (event.reservedMemberCount || 0) + (event.attendedMemberCount || 0);
  return (
    <Box sx={{ p: 0 }}>
      <TooltipLessonHeader>
        <TooltipLessonTitle>OT</TooltipLessonTitle>
        <TooltipStatusBadge badgeBg={status.badge} badgeFont={status.font}>
          {status.label}
        </TooltipStatusBadge>
      </TooltipLessonHeader>
      <TooltipBody>
        <TooltipInfoRow>
          <TooltipInfoLabel>시간</TooltipInfoLabel>
          <TooltipInfoValue>
            {event.startTime} - {event.endTime}
          </TooltipInfoValue>
        </TooltipInfoRow>
        <TooltipInfoRow>
          <TooltipInfoLabel>강사</TooltipInfoLabel>
          <TooltipInfoValue>{event.trainerName}</TooltipInfoValue>
        </TooltipInfoRow>
        {event.branchExerciseRoomName && (
          <TooltipInfoRow>
            <TooltipInfoLabel>강의실</TooltipInfoLabel>
            <TooltipInfoValue>{event.branchExerciseRoomName}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
        <TooltipInfoRow>
          <TooltipInfoLabel>예약</TooltipInfoLabel>
          <TooltipInfoValue valueColor="#0F7F85">
            {currentCount} / {event.membersMaxCount}명
          </TooltipInfoValue>
        </TooltipInfoRow>
        <TooltipInfoRow>
          <TooltipInfoLabel>출석</TooltipInfoLabel>
          <TooltipInfoValue>{event.attendedMemberCount || 0}명</TooltipInfoValue>
        </TooltipInfoRow>
        <TooltipInfoRow>
          <TooltipInfoLabel>결석</TooltipInfoLabel>
          <TooltipInfoValue>{event.absentedMemberCount || 0}명</TooltipInfoValue>
        </TooltipInfoRow>
      </TooltipBody>
    </Box>
  );
};

/**
 * 여러 명 OT 카드 — 그룹레슨 카드처럼 수업 한 장(회원별 분할 없음), 인원 n/정원 뱃지.
 * 배경은 OT 색, 클릭하면 메뉴(참석관리=그룹 참석관리 모달)가 열리고 드래그로 시간 이동이 된다.
 */
const OtGroupCardComponent = ({ event, position, onClick, onContextMenu, onDragStart, isDragging, splitInfo, typeFilter }) => {
  // OT 만 보는 중이면 배경을 수업 상태색(개인레슨 카드와 동일)으로 칠한다
  const meta = getFilteredStatusCardMeta(event, typeFilter) || {
    ...ATTEND_STATUS_META[getLessonAttendStatus(event)],
    dayBg: OT_CARD_META.dayBg,
    dayBorder: OT_CARD_META.dayBorder,
  };
  const totalMemberCount =
    (event.reservedMemberCount || 0) + (event.attendedMemberCount || 0) + (event.absentedMemberCount || 0);
  const total = splitInfo?.total || 1;
  const splitIndex = splitInfo?.index || 0;

  return (
    <Tooltip title={<OtGroupTooltipContent event={event} />} {...OT_GROUP_TOOLTIP_PROPS}>
      <GroupLessonCard
        className="event-card"
        data-class-time-id={event.courseClassTimeId}
        data-course-class-id={event.courseClassId}
        bgColor={meta.dayBg}
        cardHeight={position.height}
        cardTop={position.top}
        cardMinHeight={position.minHeight}
        cardWidth={total > 1 ? `${100 / total}%` : '100%'}
        cardLeft={total > 1 ? `${(splitIndex * 100) / total}%` : 0}
        cardZIndex={2 + splitIndex}
        sx={dayCardFrameStyle(meta, isDragging ? { opacity: 0.4, cursor: 'grabbing' } : { cursor: 'grab' })}
        onMouseDown={e => {
          e.stopPropagation();
          onDragStart?.(e, event);
        }}
        onClick={e => onClick(e)}
        onContextMenu={e => onContextMenu?.(e, event)}>
        <DayCardTopRow
          meta={meta}
          timeText={`${event.startTime} - ${event.endTime}`}
          statusLabel={EVENT_TYPE_META.ot.label}
          rightBadge={
            <DayCardStatusBadge className="dc-badge dc-session" badgeBg={meta.badgeBg} badgeFont={meta.badgeFont}>
              {totalMemberCount}/{event.membersMaxCount}명
            </DayCardStatusBadge>
          }
        />
        <DayCardSubRow meta={meta} name={getEventDisplayName(event)} roomName={event.branchExerciseRoomName} />
      </GroupLessonCard>
    </Tooltip>
  );
};

/**
 * 상담 카드 — 개인레슨 카드와 같은 2행 프레임을 쓴다.
 * 1행 [결과 뱃지][시간][대상 칩], 2행 [이름 / 상담].
 * 배경은 상담 공통색 하나이고 결과는 뱃지가 전담한다. 카드가 겹쳐 좁아져도 뱃지와 대상 칩은 접지 않는다.
 */
const ConsultationCardComponent = ({
  event,
  position,
  isPast,
  onClick,
  onContextMenu,
  onDragStart,
  isDragging,
  splitInfo,
  typeFilter,
}) => {
  const resultMeta = CONSULT_RESULT_META[event.consultResult] || CONSULT_RESULT_META.SCHEDULED;
  // 상담만 보는 중이면 배경이 결과색(카운트 칩과 동일)을 전담하고 결과 뱃지는 흰 반투명으로 물러난다
  const filteredMeta = getFilteredStatusCardMeta(event, typeFilter);
  const meta = { ...CONSULT_CARD_META, ...resultMeta, ...filteredMeta };
  const targetLabel = CONSULT_TARGET_META[event.consultType]?.label || CONSULT_TARGET_META.MEMBER.label;

  const total = splitInfo?.total || 1;
  const splitIndex = splitInfo?.index || 0;

  const consultCardStyle = {
    // 지난 상담은 옅게 — 색을 바꾸면 결과 뱃지와 경쟁한다.
    // 다만 hover 로 끌어올린 동안은 불투명해야 한다 (반투명이면 아래 깔린 카드가 비쳐 읽히지 않는다).
    ...(isPast && { opacity: 0.72, '&:hover': { opacity: 1 } }),
    ...(isDragging ? { opacity: 0.4, cursor: 'grabbing' } : { cursor: 'grab' }),
    // 두 줄이 붙어 겹쳐 보이던 간격을 넓히고 본문 행간도 키운다
    gap: '4px',
    // 결과 뱃지는 원래 구조 그대로 반 단계만 키운다 — 행 높이 초과는 cqh 상한이 막는다
    '.dc-badge': {
      fontSize: 'max(10.5px, min(1.12em, 44cqh))',
      padding: '2px 8px',
    },
    '.dc-sub': { fontSize: 'max(9px, min(0.92em, 32cqh))', lineHeight: 1.35 },
    // 겹쳐서 좁아져도 결과·대상은 접지 않는다 (프레임 기본 규칙을 상담에서만 되돌린다)
    // 대상 칩은 결과 뱃지(.dc-badge)와 같은 크기 규격을 쓴다
    '.dc-target': {
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: 'max(9.5px, min(1em, 38cqh))',
      lineHeight: 1.3,
      padding: '1px 7px',
    },
    // 폭이 부족하면 시간을 먼저 접는다 — 시간은 카드의 세로 위치로 읽히지만 결과·대상은 대체 수단이 없다
    '@container (max-width: 165px)': {
      '.dc-time': { display: 'none' },
    },
    ...(total === 2 && { fontSize: '0.68rem' }),
    ...(total >= 3 && { padding: '2px 5px', fontSize: '0.62rem' }),
  };

  return (
    <LessonHoverTooltip event={event}>
      <ConsultationCard
        className="event-card"
        data-consultation-id={event.consultationId}
        bgColor={meta.dayBg}
        cardHeight={position.height}
        cardTop={position.top}
        cardMinHeight={position.minHeight}
        cardWidth={total > 1 ? `${100 / total}%` : '100%'}
        cardLeft={total > 1 ? `${(splitIndex * 100) / total}%` : 0}
        cardZIndex={2 + splitIndex}
        sx={dayCardFrameStyle(meta, consultCardStyle)}
        onMouseDown={e => {
          e.stopPropagation();
          onDragStart?.(e, event);
        }}
        onClick={e => onClick(e)}
        onContextMenu={e => onContextMenu?.(e, event)}>
        <DayCardTopRow
          meta={meta}
          timeText={total >= 2 ? event.startTime : `${event.startTime} - ${event.endTime}`}
          statusLabel={meta.shortLabel}
          rightBadge={<ConsultTargetChip className="dc-target">{targetLabel}</ConsultTargetChip>}
        />
        <DayCardSubRow meta={meta} name={event.memberName || '-'} roomName={event.memberPhoneNumber} />
      </ConsultationCard>
    </LessonHoverTooltip>
  );
};

/**
 * 기타 일정 카드 — 지점 내부 일정(정수기 점검·외출 등). 상담 카드 프레임을 그대로 쓰고
 * 색과 내용만 다르다. 회원·정원·상태가 없어 2행은 제목 하나로 채운다.
 */
const EtcScheduleCardComponent = ({ event, position, isPast, onClick, onContextMenu, onDragStart, isDragging, splitInfo }) => {
  const meta = ETC_CARD_META;
  const total = splitInfo?.total || 1;
  const splitIndex = splitInfo?.index || 0;

  const etcCardStyle = {
    // 지난 일정 흐림은 hover 로 끌어올린 동안 해제한다 (상담 카드와 같은 규칙)
    ...(isPast && { opacity: 0.72, '&:hover': { opacity: 1 } }),
    ...(isDragging ? { opacity: 0.4, cursor: 'grabbing' } : { cursor: 'grab' }),
    gap: '4px',
    '.dc-badge': { fontSize: 'max(10.5px, min(1.12em, 44cqh))', padding: '2px 8px' },
    '.dc-sub': { fontSize: 'max(9px, min(0.92em, 32cqh))', lineHeight: 1.35 },
    '@container (max-width: 165px)': {
      '.dc-time': { display: 'none' },
    },
    ...(total === 2 && { fontSize: '0.68rem' }),
    ...(total >= 3 && { padding: '2px 5px', fontSize: '0.62rem' }),
  };

  return (
    <LessonHoverTooltip event={event}>
      <ConsultationCard
        className="event-card"
        data-branch-schedule-id={event.branchScheduleId}
        bgColor={meta.dayBg}
        cardHeight={position.height}
        cardTop={position.top}
        cardMinHeight={position.minHeight}
        cardWidth={total > 1 ? `${100 / total}%` : '100%'}
        cardLeft={total > 1 ? `${(splitIndex * 100) / total}%` : 0}
        cardZIndex={2 + splitIndex}
        sx={dayCardFrameStyle(meta, etcCardStyle)}
        onMouseDown={e => {
          e.stopPropagation();
          onDragStart?.(e, event);
        }}
        onClick={e => onClick(e)}
        onContextMenu={e => onContextMenu?.(e, event)}>
        <DayCardTopRow
          meta={meta}
          timeText={total >= 2 ? event.startTime : `${event.startTime} - ${event.endTime}`}
          statusLabel={meta.shortLabel}
        />
        <DayCardSubRow meta={meta} name={event.className || '-'} />
      </ConsultationCard>
    </LessonHoverTooltip>
  );
};

/**
 * 메인 이벤트 카드 컴포넌트 (타입별 분기)
 */
export const EventCard = ({
  event,
  position,
  currentDate,
  onClick,
  onContextMenu,
  onDragStart,
  isDragging,
  splitInfo,
  ticketAlertCount,
  ticketAlertCountUnit,
  typeFilter,
}) => {
  const now = moment();
  const eventEndTime = moment(`${event.registrationStartDate} ${event.endTime}`, 'YYYY-MM-DD HH:mm');
  const isPast = now.isAfter(eventEndTime);
  const duration = calculateDuration(event.startTime, event.endTime);
  const isLongLesson = duration >= 50;

  const commonProps = {
    event,
    position,
    isPast,
    isLongLesson,
    onClick,
    onContextMenu,
    onDragStart,
    isDragging,
    ticketAlertCount,
    ticketAlertCountUnit,
    splitInfo,
    typeFilter,
  };

  switch (event.courseClassType) {
    case 'GROUP_LESSON':
      return <GroupLessonCardComponent {...commonProps} />;
    case 'CONSULTATION':
      return <ConsultationCardComponent {...commonProps} />;
    case 'ETC':
      return <EtcScheduleCardComponent {...commonProps} />;
    case 'DUET_LESSON':
      // 듀엣은 출석 상태색 그대로·배지 없음. 9/29 OT 배지 작업 때 OT 와 같은 분기로 묶여 OT 색·배지가 붙었었다.
      return <DuetLessonCardComponent {...commonProps} />;
    case 'OT':
      if (isOtGroupEvent(event)) return <OtGroupCardComponent {...commonProps} />;
      // 1명 OT 는 듀엣처럼 회원마다 카드 한 장 (members 는 예약행 기준). 배경은 OT 색, 출석 상태는 카드 안 배지로 구분
      // (OT 만 보는 중이면 배경도 상태색)
      return (
        <DuetLessonCardComponent
          {...commonProps}
          cardMeta={
            getFilteredStatusCardMeta(event, typeFilter) ? null : { dayBg: OT_CARD_META.dayBg, dayBorder: OT_CARD_META.dayBorder }
          }
          typeBadge={
            <DayCardStatusBadge
              className="dc-badge"
              badgeBg="#fff"
              badgeFont={EVENT_TYPE_META.ot.color}
              sx={{ ml: '4px', flexShrink: 0 }}>
              {EVENT_TYPE_META.ot.label}
            </DayCardStatusBadge>
          }
        />
      );
    default:
      return <PrivateLessonCardComponent {...commonProps} />;
  }
};

/**
 * 이벤트 카드들 렌더링 함수 (일일/주간 공통)
 */
export const renderEventCards = ({
  events,
  hourIndex,
  filterFn,
  periodTimes,
  currentDate,
  onEventClick,
  onEventContextMenu,
  onEventDragStart,
  dragEvent,
  ticketAlertCount,
  ticketAlertCountUnit,
  typeFilter,
  cellHeight,
  slotHours = 1,
  cardMinHeightPx,
}) => {
  const filteredEvents = events.filter(filterFn);

  // 겹치는 이벤트(수업·상담 불문)를 클러스터로 묶어 좌우 컬럼 분할 — 주간 보기와 동일 규칙
  const overlapLayout = layoutDayEvents(filteredEvents);

  // 짧은 카드의 최소 높이 때문에 같은 시간 셀 안에서 카드가 겹칠 수 있다. 늦게 시작한 카드가 위에
  // 오도록 그리는 순서만 시작시각 순으로 맞춘다 (idx 는 클릭 핸들러가 쓰므로 원래 값을 유지).
  const orderedEvents = filteredEvents
    .map((event, idx) => ({ event, idx }))
    .sort((a, b) => (a.event.startTime || '').localeCompare(b.event.startTime || ''));

  // 뒤 카드가 앞 카드의 시간 줄까지 덮지 않도록 아래로 밀어낼 픽셀
  const stackOffsets = layoutStackedOffsets(filteredEvents, cellHeight, MIN_CARD_EXPOSED_HEIGHT);

  return orderedEvents.map(({ event, idx }) => {
    const position = calculateEventPosition(
      event,
      hourIndex,
      periodTimes,
      stackOffsets.get(event) || 0,
      slotHours,
      cardMinHeightPx,
    );
    if (!position) return null;

    // 상담은 courseClassTimeId 가 없어 undefined === undefined 오판이 났다 — 접두어 key 로 비교
    const isDragging = dragEvent ? getEventCardKey(dragEvent, 'drag') === getEventCardKey(event, 'event') : false;

    const { col, cols } = overlapLayout.get(event) || { col: 0, cols: 1 };
    const splitInfo = cols > 1 ? { total: cols, index: col } : null;

    return (
      <EventCard
        key={getEventCardKey(event, `event-${idx}`)}
        event={event}
        position={position}
        currentDate={currentDate}
        onClick={(e, member) => onEventClick(e, event, idx, member)}
        onContextMenu={onEventContextMenu}
        onDragStart={onEventDragStart}
        isDragging={isDragging}
        splitInfo={splitInfo}
        ticketAlertCount={ticketAlertCount}
        ticketAlertCountUnit={ticketAlertCountUnit}
        typeFilter={typeFilter}
      />
    );
  });
};

export default EventCard;
